import { useEffect, useState } from "react";
import type {Bc3AuxiliaryComposition} from "../domain/bc3ImportPlan";
import { supabase } from "../lib/supabase";
import { collectPaginatedRows } from "../lib/paginatedRows";

export type ArticleBase = {
  id: string; name: string; version: number;
  parameter_values: Record<string, string>;
  source_kind: string; source_url: string | null; source_observed_at: string | null;
  review_status: string; reference_unit_price: number | null;
  metadata: { source_code?: string; bc3_import?:{source_encoding?:string;declared_encoding?:string;auxiliary_compositions?:Bc3AuxiliaryComposition[];kind:string;batch_id:string;namespace:string;file_name:string;source_owner:string;source_version:string;tariff_index:number;declared_dates:string[];fingerprint:string}; chapter_path?:string[]; configuration_facts?: {label:string;value:string}[]; technical_sections?: {title:string;text:string}[]; price_observation_note?:string; price_review?: {kind:string;parent_base_id:string;price_date:string;note:string;reviewed_at:string;reviewed_by:string}; scope_notes?: string; source_height_condition?: string;
    reference_lines?: { code: string; label: string; unit: string; quantity: number; unit_price: number; partial: number }[] };
};
const euro = (n: number | null) => n === null ? "—" : Number(n).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
export default function ArticleBasePicker({ workCode, onApply }: {
  workCode: string; onApply: (base: ArticleBase) => void;
}) {
  const [rows, setRows] = useState<ArticleBase[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let cancelled = false; setBusy(true); setError("");
    void collectPaginatedRows<ArticleBase>((from, to) => supabase.from("parametric_article_bases")
      .select("*,work_items!inner(code)").eq("work_items.code", workCode)
      .neq("review_status", "archived").order("name").order("version", { ascending: false }).order("id").range(from, to))
      .then(({ data, error: problem }) => {
        if (cancelled) return;
        if (problem) { setRows([]); setError(problem.message); } else setRows(data ?? []);
      }).catch((cause) => { if (!cancelled) setError(String(cause.message ?? cause)); }).finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [workCode]);
  return <section className="card engine-parameters">
    <h3>Começar com um artigo-base</h3>
    <p className="muted">Carregue uma configuração e adapte os campos à obra. Carregar a base substitui os parâmetros atuais.</p>
    {busy ? <p>A carregar bases…</p> : null}
    {error ? <div className="alert alert-error">{error}</div> : null}
    {!busy && !error && !rows.length ? <p>A biblioteca ainda está a ser preparada para esta unidade.</p> : null}
    {rows.map((base) => <article key={base.id}>
      <h4>{base.name} · v{base.version}</h4>
      <p>{base.source_kind === "cype_reference" ? "Referência CYPE" : "Base L37"} · {base.review_status === "pending" ? "Por rever" : "Revista"}</p>
      <p>PU da configuração de origem: <strong>{euro(base.reference_unit_price)}</strong></p>
      {base.metadata.source_height_condition ? <p>{base.metadata.source_height_condition}</p> : null}
      <p className="muted">{base.metadata.scope_notes}</p>
      {base.source_url ? <a href={base.source_url} target="_blank" rel="noreferrer">Consultar origem</a> : null}
      {base.source_observed_at ? <p className="muted">Consultado em {new Date(base.source_observed_at).toLocaleDateString("pt-PT")}</p> : null}
      <button className="button button-primary" type="button" onClick={() => onApply(base)}>Carregar parâmetros da base</button>
      <details><summary>Composição e preços de referência</summary>
        <p className="muted">Valores da configuração de origem. Alterar os parâmetros no Gerador não recalcula esta tabela de referência.</p>
        {(base.metadata.reference_lines ?? []).map((line) => <div className="estimate-history-row" key={line.code}>
          <span>{line.label}</span><span>{line.quantity.toLocaleString("pt-PT")} {line.unit}</span>
          <span>{euro(line.unit_price)}</span><strong>{euro(line.partial)}</strong>
        </div>)}
      </details>
    </article>)}
  </section>;
}
